import { createSignal, type Component, For, Show } from "solid-js";
import { Drawer } from "../shared/drawer";
import "./hosts.css";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import type { ColumnDef } from "@tanstack/solid-table";
import { PanelLayout, type PanelStatus } from "../shared/panel-base";
import { DataTable } from "../shared/data-table";
import { registerPanel } from "../panel-registry";
import { useWs } from "../shared/ws";
import { apiFetch } from "../shared/api-fetch";

interface HostItem {
  hostId: string;
  hostname?: string;
  platform?: string;
  version?: string;
  displayName?: string;
  tags?: string[];
  systemInfo?: Record<string, unknown> | null;
  lastSeen?: string;
  lastSeenAgeMs?: number | null;
  status?: string;
  online?: boolean;
  components?: Record<string, string> | null;
  persistentSessionCode?: string | null;
}

interface HostPlugin {
  id: string;
  channel?: string;
  version?: string | null;
}

interface HostDetail extends HostItem {
  plugins?: HostPlugin[];
}

interface HostListResp {
  items?: HostItem[];
}

const POLL_MS = 15_000;
const ONLINE_THRESHOLD_MS = 90_000;

function formatDate(value: string | undefined): string {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function computeOnline(host: HostItem): boolean {
  if (typeof host.online === "boolean") return host.online;
  if (!host.lastSeen) return false;
  try {
    const ts = new Date(host.lastSeen).getTime();
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < ONLINE_THRESHOLD_MS;
  } catch {
    return false;
  }
}

function displayLabel(host: HostItem): string {
  return host.displayName || host.hostname || host.hostId;
}

interface DebugVerdict {
  code?: string;
  summary?: string;
  relay?: { tcp?: string; tls?: string; ws?: string; http?: number };
  site?: { tcp?: string; http?: number };
}

/** Lit le mode debug rapporté par le host au heartbeat (systemInfo.debugMode). */
function debugModeOf(host: HostItem): { active: boolean; untilMs: number; verdict: DebugVerdict | null } | null {
  const dm = (host.systemInfo as Record<string, unknown> | null | undefined)?.["debugMode"] as
    | { active?: boolean; untilMs?: number; verdict?: DebugVerdict }
    | undefined;
  if (!dm || !dm.active) return null;
  return { active: true, untilMs: Number(dm.untilMs) || 0, verdict: dm.verdict || null };
}

/** « DEBUG (12m) » / « DEBUG (45s) » / « DEBUG (expiré) » selon l'échéance. */
function formatDebugLabel(untilMs: number): string {
  const msLeft = untilMs - Date.now();
  if (msLeft <= 0) return "DEBUG (expiré)";
  const s = Math.ceil(msLeft / 1000);
  if (s < 90) return `DEBUG (${s}s)`;
  return `DEBUG (${Math.ceil(s / 60)}m)`;
}

async function fetchHosts(): Promise<HostItem[]> {
  const resp = await apiFetch("/host/list", {
    method: "GET",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = (await resp.json()) as HostListResp;
  return Array.isArray(data.items) ? data.items : [];
}

async function fetchHostDetail(hostId: string): Promise<HostDetail | null> {
  // Le détail enrichi (status, components, plugins) est servi par le backend
  // sous /api/host/:id — le legacy /host/:id n'existe pas (404).
  const resp = await apiFetch(`/api/host/${encodeURIComponent(hostId)}`, {
    method: "GET",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = (await resp.json()) as { ok?: boolean; host?: HostDetail };
  return data.host || null;
}

/** Formate une ancienneté en ms → « il y a 18 s » / « il y a 4 min ». */
function formatAge(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const s = Math.round(ms / 1000);
  if (s < 90) return `il y a ${s} s`;
  const m = Math.round(s / 60);
  if (m < 90) return `il y a ${m} min`;
  return `il y a ${Math.round(m / 60)} h`;
}

const HostsPanel: Component = () => {
  const [filter, setFilter] = createSignal("");
  const [connecting, setConnecting] = createSignal<Set<string>>(new Set());
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [detailHost, setDetailHost] = createSignal<HostDetail | null>(null);
  const [detailOpen, setDetailOpen] = createSignal(false);
  const [detailLoading, setDetailLoading] = createSignal(false);
  const [detailError, setDetailError] = createSignal("");
  const qc = useQueryClient();
  const ws = useWs();

  async function openDetail(host: HostItem): Promise<void> {
    setDetailOpen(true);
    setDetailHost(null);
    setDetailError("");
    setDetailLoading(true);
    try {
      const d = await fetchHostDetail(host.hostId);
      if (!d) setDetailError("Hôte introuvable.");
      setDetailHost(d);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : String(err));
    } finally {
      setDetailLoading(false);
    }
  }

  const query = createQuery(() => ({
    queryKey: ["hosts", "list"] as const,
    queryFn: fetchHosts,
    refetchInterval: POLL_MS,
    staleTime: 10_000,
  }));

  const rows = (): HostItem[] => {
    const data = query.data || [];
    const f = filter().trim().toLowerCase();
    if (!f) return data;
    return data.filter(
      (h) =>
        String(h.hostId || "")
          .toLowerCase()
          .includes(f) ||
        String(h.hostname || "")
          .toLowerCase()
          .includes(f) ||
        String(h.displayName || "")
          .toLowerCase()
          .includes(f) ||
        (h.tags || []).some((t) => t.toLowerCase().includes(f)),
    );
  };

  const status = (): PanelStatus => {
    if (actionStatus()) return actionStatus()!;
    if (query.isError) return { text: "Erreur reseau", level: "danger" };
    if (query.isLoading) return { text: "Chargement...", level: "info" };
    const data = query.data || [];
    const online = data.filter(computeOnline).length;
    return { text: `${data.length} hote(s), ${online} en ligne.`, level: "info" };
  };

  async function connectHost(host: HostItem): Promise<void> {
    if (!host.hostId || connecting().has(host.hostId)) return;

    let sessionCode = host.persistentSessionCode;

    // Si le code n'est pas encore disponible côté client, appeler ensure-session.
    if (!sessionCode) {
      setConnecting((prev) => new Set(prev).add(host.hostId));
      try {
        const resp = await apiFetch(`/host/${encodeURIComponent(host.hostId)}/ensure-session`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json", accept: "application/json" },
          body: "{}",
        });
        const data = (await resp.json().catch(() => ({}))) as { ok?: boolean; sessionCode?: string; error?: string };
        if (!resp.ok || !data.ok || !data.sessionCode) {
          setActionStatus({ text: `Echec: ${data.error || resp.status}`, level: "danger" });
          return;
        }
        sessionCode = data.sessionCode;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setActionStatus({ text: `Erreur: ${msg}`, level: "danger" });
        return;
      } finally {
        setConnecting((prev) => {
          const next = new Set(prev);
          next.delete(host.hostId);
          return next;
        });
        qc.invalidateQueries({ queryKey: ["hosts", "list"] });
      }
    }

    // Rejoindre la session directement via le WS opérateur courant.
    ws.send({ type: "session:join", role: "operator", sessionCode });
    setActionStatus({ text: `Connexion a ${displayLabel(host)}...`, level: "info" });
    setTimeout(() => setActionStatus(null), 3000);
  }

  const columns: ColumnDef<HostItem>[] = [
    {
      header: "Hote",
      id: "label",
      accessorFn: displayLabel,
      cell: (info) => {
        const host = info.row.original;
        const label = displayLabel(host);
        const sub = host.displayName && host.hostname ? host.hostname : null;
        return (
          <span>
            <span style={{ "font-weight": "600" }}>{label}</span>
            {sub && <span style={{ "font-size": "0.85em", opacity: "0.65", "margin-left": "0.4em" }}>{sub}</span>}
          </span>
        );
      },
    },
    { header: "Plateforme", accessorKey: "platform" },
    {
      header: "Tags",
      id: "tags",
      accessorFn: (row) => (row.tags || []).join(", "),
      cell: (info) => {
        const tags = info.row.original.tags || [];
        return (
          <Show when={tags.length > 0} fallback={<span style={{ opacity: "0.4" }}>—</span>}>
            <span>
              <For each={tags}>
                {(t) => (
                  <span
                    style={{
                      background: "var(--color-muted, #e5e7eb)",
                      "border-radius": "3px",
                      padding: "1px 6px",
                      "font-size": "0.8em",
                      "margin-right": "4px",
                    }}
                  >
                    {t}
                  </span>
                )}
              </For>
            </span>
          </Show>
        );
      },
    },
    {
      header: "Etat",
      id: "online",
      accessorFn: (row) => (computeOnline(row) ? "online" : "offline"),
      cell: (info) => {
        const isOnline = computeOnline(info.row.original);
        const dbg = debugModeOf(info.row.original);
        return (
          <span style={{ display: "inline-flex", gap: "5px", "align-items": "center" }}>
            <span class={`badge ${isOnline ? "badge--ok" : "badge--muted"}`}>
              {isOnline ? "en ligne" : "hors ligne"}
            </span>
            <Show when={dbg}>
              {(d) => (
                <span class="badge badge--debug" title="Agent en mode debug (relay interne loopback)">
                  {formatDebugLabel(d().untilMs)}
                </span>
              )}
            </Show>
          </span>
        );
      },
    },
    {
      header: "Derniere vue",
      accessorKey: "lastSeen",
      cell: (info) => formatDate(info.getValue() as string | undefined),
    },
  ];

  const toolbar = (
    <>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre (nom / hostname / tag)..."
        spellcheck={false}
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <button
        type="button"
        class="btn btn--xs btn--info"
        onClick={() => qc.invalidateQueries({ queryKey: ["hosts", "list"] })}
      >
        Rafraichir
      </button>
    </>
  );

  return (
    <>
      <PanelLayout title="Hotes persistants" toolbar={toolbar} status={status()}>
        <DataTable
          rows={rows()}
          columns={columns}
          emptyMessage="Aucun hote enregistre."
          rowActions={(host) => {
            const online = computeOnline(host);
            const busy = connecting().has(host.hostId);
            const hasCode = !!host.persistentSessionCode;
            return (
              <>
                <button
                  type="button"
                  class="btn btn--xs btn--ghost"
                  onClick={() => void openDetail(host)}
                  title="Santé, versions des exe et des plugins"
                >
                  Détail
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--primary"
                  disabled={!online || busy}
                  title={!hasCode ? "Session non encore disponible" : ""}
                  onClick={() => connectHost(host)}
                >
                  {busy ? "..." : "Se connecter"}
                </button>
              </>
            );
          }}
        />
      </PanelLayout>

      <Drawer
        open={detailOpen()}
        onClose={() => setDetailOpen(false)}
        title={detailHost() ? `Hôte — ${displayLabel(detailHost()!)}` : "Hôte"}
        width="480px"
      >
        <Show when={detailLoading()}>
          <p style={{ opacity: "0.6" }}>Chargement…</p>
        </Show>
        <Show when={detailError()}>
          <p class="panel-error">{detailError()}</p>
        </Show>
        <Show when={detailHost()}>
          {(h) => (
            <div style={{ display: "flex", "flex-direction": "column", gap: "16px" }}>
              <section>
                <h4 class="host-detail__h">Santé</h4>
                <div class="host-detail__row">
                  <span>État</span>
                  <span class={`badge ${h().status === "online" ? "badge--ok" : "badge--muted"}`}>
                    {h().status || (computeOnline(h()) ? "online" : "offline")}
                  </span>
                </div>
                <div class="host-detail__row">
                  <span>Dernier heartbeat</span>
                  <span>{formatAge(h().lastSeenAgeMs)}</span>
                </div>
                <Show when={debugModeOf(h())}>
                  {(d) => (
                    <>
                      <div class="host-detail__row">
                        <span>Mode debug</span>
                        <span class="badge badge--debug" title="Relay interne loopback actif">
                          {formatDebugLabel(d().untilMs)}
                        </span>
                      </div>
                      <Show
                        when={d().verdict}
                        fallback={
                          <div class="host-detail__row">
                            <span>Diagnostic</span>
                            <span style={{ opacity: "0.6" }}>self-test en cours…</span>
                          </div>
                        }
                      >
                        {(v) => (
                          <div
                            style={{
                              padding: "8px 10px",
                              "border-radius": "6px",
                              background: "var(--warn-soft, #2a2410)",
                              "border-left": "3px solid var(--warn, #e0a83a)",
                              "font-size": "12px",
                              "line-height": "1.4",
                            }}
                          >
                            <div style={{ "font-weight": "600", color: "var(--warn, #e0a83a)" }}>{v().code || "?"}</div>
                            <div style={{ "margin-top": "3px", opacity: "0.9" }}>{v().summary || ""}</div>
                            <div
                              style={{
                                "margin-top": "5px",
                                "font-family": "monospace",
                                opacity: "0.7",
                                "font-size": "11px",
                              }}
                            >
                              relay: tcp={v().relay?.tcp || "—"} tls={v().relay?.tls || "—"} ws={v().relay?.ws || "—"}
                              {" · "}site: tcp={v().site?.tcp || "—"} http={v().site?.http ?? "—"}
                            </div>
                          </div>
                        )}
                      </Show>
                    </>
                  )}
                </Show>
                <div class="host-detail__row">
                  <span>Hostname</span>
                  <span>{h().hostname || "—"}</span>
                </div>
                <div class="host-detail__row">
                  <span>Plateforme</span>
                  <span>{h().platform || "—"}</span>
                </div>
              </section>

              <section>
                <h4 class="host-detail__h">Versions des exécutables</h4>
                <Show
                  when={h().components && Object.keys(h().components!).length}
                  fallback={<p style={{ opacity: "0.6" }}>Non rapporté (host antérieur au reporting par composant).</p>}
                >
                  <For each={Object.entries(h().components!)}>
                    {([name, ver]) => (
                      <div class="host-detail__row">
                        <span>{name}</span>
                        <code>{ver}</code>
                      </div>
                    )}
                  </For>
                </Show>
              </section>

              <section>
                <h4 class="host-detail__h">Plugins installés</h4>
                <Show
                  when={h().plugins && h().plugins!.length}
                  fallback={<p style={{ opacity: "0.6" }}>Aucun plugin.</p>}
                >
                  <For each={h().plugins!}>
                    {(p) => (
                      <div class="host-detail__row">
                        <span>
                          {p.id}
                          <Show when={p.channel && p.channel !== "stable"}>
                            <span style={{ opacity: "0.55", "margin-left": "0.4em" }}>{p.channel}</span>
                          </Show>
                        </span>
                        <code style={{ opacity: p.version ? "1" : "0.5" }}>{p.version || "N/A"}</code>
                      </div>
                    )}
                  </For>
                </Show>
              </section>
            </div>
          )}
        </Show>
      </Drawer>
    </>
  );
};

registerPanel("hosts", HostsPanel);

export default HostsPanel;
